import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bell, Check, RefreshCw, ArrowUpRight } from "lucide-react";
import { useNotifications } from "../../hooks/useNotifications";

const parseISO = (iso) => {
  if (!iso) return null;
  const s = String(iso).trim();
  const hasTZ = /[zZ]$|[+-]\d{2}:\d{2}$/.test(s);
  const d = new Date(hasTZ ? s : `${s}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const timeAgo = (iso) => {
  const d = parseISO(iso);
  if (!d) return "";
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const getPayload = (notification) => {
  const raw = notification?.payload || {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) || {};
    } catch {
      return {};
    }
  }
  return raw && typeof raw === "object" ? raw : {};
};

const firstValue = (payload, ...keys) => {
  for (const key of keys) {
    const value = payload?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const toNumberOrValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : value;
};

const notificationToTarget = (notification) => {
  const payload = getPayload(notification);
  const type = String(notification?.type || "").toUpperCase();

  const projectId = toNumberOrValue(firstValue(payload, "project_id", "projectId"));
  const inspectionId = toNumberOrValue(firstValue(payload, "inspection_id", "inspectionId"));
  const findingId = toNumberOrValue(firstValue(payload, "finding_id", "findingId"));
  const rfiId = toNumberOrValue(firstValue(payload, "rfi_id", "rfiId"));
  const ncrId = toNumberOrValue(firstValue(payload, "ncr_id", "ncrId"));
  const submittalId = toNumberOrValue(firstValue(payload, "submittal_id", "submittalId"));
  const walkScheduleId = toNumberOrValue(firstValue(payload, "walk_schedule_id", "walkScheduleId"));
  const equipmentId = toNumberOrValue(firstValue(payload, "equipment_id", "equipmentId"));
  const equipmentTaskId = toNumberOrValue(
    firstValue(payload, "equipment_task_id", "equipmentTaskId", "task_id", "taskId"),
  );

  if (equipmentTaskId || equipmentId || type.includes("EQUIPMENT")) {
    return {
      tab: "equipment",
      action: equipmentTaskId ? "openEquipmentTask" : "openEquipment",
      projectId,
      equipmentId,
      equipmentTaskId,
    };
  }

  if (findingId || type.includes("FINDING") || type.includes("ISSUE")) {
    return {
      tab: "my-issues",
      action: "openFinding",
      projectId,
      inspectionId,
      findingId,
    };
  }

  if (inspectionId || type.includes("INSPECTION")) {
    return {
      tab: "inspections",
      action: "openInspection",
      projectId,
      inspectionId,
    };
  }

  if (rfiId || type.includes("RFI")) {
    return { tab: "rfi", action: "openRfi", projectId, rfiId };
  }

  if (ncrId || type.includes("NCR")) {
    return { tab: "ncr", action: "openNcr", projectId, ncrId };
  }

  if (submittalId || type.includes("SUBMITTAL")) {
    return { tab: "submittals", action: "openSubmittal", projectId, submittalId };
  }

  if (walkScheduleId || type.includes("WALK")) {
    return {
      tab: "walk-scheduler",
      action: "openWalkSchedule",
      projectId,
      walkScheduleId,
      inspectionId,
    };
  }

  if (projectId) {
    return { tab: "dashboard", action: "openProject", projectId };
  }

  return { tab: "dashboard", action: "openNotification" };
};

export default function NotificationBell({ onNotificationNavigate }) {
  const [open, setOpen] = useState(false);
  const notificationRef = useRef(null);
  const { items, unread, loading, marking, error, reload, markRead, markAllRead } =
    useNotifications({ limit: 50, pollMs: 15000 });

  const sorted = useMemo(() => {
    const arr = Array.isArray(items) ? [...items] : [];
    arr.sort((a, b) => {
      if (!!a.is_read !== !!b.is_read) return a.is_read ? 1 : -1;
      const da = parseISO(a.created_at);
      const db = parseISO(b.created_at);
      return (db?.getTime() || 0) - (da?.getTime() || 0);
    });
    return arr;
  }, [items]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutsidePointerDown = (event) => {
      const container = notificationRef.current;
      if (container && !container.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handleOutsidePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handleOutsidePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    if (!notification.is_read) {
      const saved = await markRead(notification.id);
      if (!saved) return;
    }
    setOpen(false);
    const target = notificationToTarget(notification);
    onNotificationNavigate?.(target, notification);
  };

  return (
    <div ref={notificationRef} className="relative">
      <button
        className="relative inline-flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100"
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[90vw] bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
            <div>
              <div className="text-sm font-semibold text-gray-900">Notifications</div>
              <div className="text-xs text-gray-500">{unread} unread</div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  reload();
                }}
                className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 border border-gray-300 text-xs hover:bg-gray-200"
                disabled={loading || marking}
                title="Refresh"
                type="button"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  markAllRead();
                }}
                disabled={marking || unread === 0}
                type="button"
                className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 border border-gray-300 text-xs hover:bg-gray-200"
              >
                {marking ? "Saving…" : "Mark all read"}
              </button>
            </div>
          </div>

          {error && (
            <div className="px-4 py-2 text-sm text-red-700 bg-red-50 border-b">
              {error}
            </div>
          )}

          <div className="border-b px-4 py-2 text-xs text-gray-500">
            Showing up to 50 notifications, unread first. Mark all read includes older notifications.
          </div>
          <div className="max-h-[420px] overflow-auto">
            {sorted.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-gray-500">
                No notifications yet.
              </div>
            ) : (
              sorted.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => handleNotificationClick(n)}
                  disabled={marking}
                  className={`group block w-full border-b px-4 py-3 text-left transition hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400 ${
                    n.is_read ? "bg-white" : "bg-orange-50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        {!n.is_read && (
                          <span className="mt-0.5 h-2 w-2 shrink-0 rounded-full bg-orange-600" />
                        )}
                        <div className="truncate text-sm font-medium text-gray-900">
                          {n.type || "Notification"}
                        </div>
                      </div>
                      <div className="mt-0.5 text-sm text-gray-700">{n.message}</div>
                      <div className="mt-1 text-xs text-gray-500">{timeAgo(n.created_at)}</div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2 text-orange-700">
                      {!n.is_read && <Check className="h-4 w-4" aria-hidden="true" />}
                      <ArrowUpRight className="h-4 w-4 opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
